import {ServerAuthProviderResponse} from "@/components/providers/server/AuthProvider";
import {removeLocaleFromPathname} from "@/utils/locales";

export type NavigationItem = {
    name: string;
    linkType: 'Internal' | 'External' | 'No Link';
    openInNewTab: 'Yes' | 'No';
    noFollow: 'Yes' | 'No';
    externalLink?: string;
    pathname?: string;
    entity?: {
        value: {
            id: string;
            slug: string;
            title: string;
        }
    };
    audiences?: {
        relationTo: 'audiences';
        value: {
            id: string;
            slug: 'guest' | 'user' | 'business' | 'agent' | 'admin';
            name: string;
            createdAt: string;
            updatedAt: string;
        };
    }[];
    children?: NavigationItem[];
    id: string;
};

export function getNavigationItemPathname(item: NavigationItem): string | null {
    if (item.linkType !== "Internal") {
        return null;
    }

    const pathname = item.entity?.value?.slug || item.pathname;
    return pathname ? normalizeNavigationPathname(pathname) : null;
}

export function normalizeNavigationPathname(pathname: string): string {
    const [pathnameWithoutQuery = "/"] = pathname.split(/[?#]/);
    const pathnameWithoutLocale = removeLocaleFromPathname(
        pathnameWithoutQuery.startsWith("/")
            ? pathnameWithoutQuery
            : `/${pathnameWithoutQuery}`,
    );
    const normalizedPathname = pathnameWithoutLocale
        .replace(/\/{2,}/g, "/")
        .replace(/\/+$/, "");

    return normalizedPathname || "/";
}

export function isNavigationItemCurrent(
    item: NavigationItem,
    currentPathname: string,
): boolean {
    const itemPathname = getNavigationItemPathname(item);
    return itemPathname !== null
        && itemPathname === normalizeNavigationPathname(currentPathname);
}

export function isNavigationItemActive(
    item: NavigationItem,
    currentPathname: string,
): boolean {
    const normalizedCurrentPathname = normalizeNavigationPathname(currentPathname);
    const itemPathname = getNavigationItemPathname(item);
    const isOwnPathActive = itemPathname !== null && (
        itemPathname === normalizedCurrentPathname
        || (
            itemPathname !== "/"
            && normalizedCurrentPathname.startsWith(`${itemPathname}/`)
        )
    );

    return isOwnPathActive || Boolean(
        item.children?.some((child) =>
            isNavigationItemActive(child, normalizedCurrentPathname)
        ),
    );
}

export function filterNavigation(
    nav: any,
    auth: ServerAuthProviderResponse
): NavigationItem[] {
    return nav
        .map((item: any) => {
            const filteredChildren = item.children
                ? filterNavigation(item.children, auth)
                : [];

            if (!isVisibleToAuth(item, auth)) {
                return null;
            }

            return {
                ...item,
                children: filteredChildren,
            };
        })
        .filter(Boolean) as NavigationItem[];
}

function isVisibleToAuth(item: NavigationItem, auth: ServerAuthProviderResponse): boolean {

    if (item.audiences && item.audiences.length > 0) {
        return item.audiences.some((a) => {
            const slug = a.value?.slug;
            switch (slug) {
                case 'guest': return !auth.isLoggedIn;
                case 'user': return auth.isLoggedIn;
                case 'business': return auth.hasPermBusiness;
                case 'agent': return auth.hasPermAgent;
                case 'admin': return auth.hasPermAdmin;
                default: return false;
            }
        });
    }

    return true;

}
